/**
 * useVersions Hook — Xyden Leadership Assessment Platform
 * Step 6F Green Phase
 */

import { useState, useEffect, useCallback } from 'react';
import { versionService } from '../../services/versionService';
import type { Version } from '../../types';

export interface UseVersionsReturn {
  data: Version[] | null;
  isLoading: boolean;
  error: Error | null;
  activeVersions: Version[] | null;
  refetch: () => Promise<void>;
}

export function useVersions(): UseVersionsReturn {
  const [data, setData] = useState<Version[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchVersions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const versions = await versionService.getAll();
      setData(versions);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  const activeVersions = data ? data.filter(v => v.state === 'Active') : null;

  return { data, isLoading, error, activeVersions, refetch: fetchVersions };
}

export default useVersions;
